/**
 * Firestore collection/document paths — the ONE place these strings live.
 * Import `COLLECTIONS` and the helpers; never hardcode a collection name.
 */
export const COLLECTIONS = {
    users: 'users',
    registrationRequests: 'registration_requests',
    products: 'products',
    activityLogs: 'activity_logs',
    pushCampaigns: 'push_campaigns',
    appConfig: 'app_config',
    presence: 'presence',
    /**
     * Free-tier admin identity. On the Spark plan there are no Cloud Functions to
     * set custom claims, so admin status is a doc here: `admins/{uid}`. Bootstrap
     * the first one in the Firebase console. Security rules check its existence.
     */
    admins: 'admins',
    /**
     * Email-OTP login state, written ONLY by the Apps Script service account
     * (docs keyed by sha256hex(email); holds the hashed code, expiry, attempt
     * counters). Never client-readable — rules deny all access.
     */
    emailOtps: 'email_otps',
    /** Admin allow-list for price visibility: `price_viewers/{uid}`. */
    priceViewers: 'price_viewers',
    /** Per-product price columns, readable only by allow-listed users. */
    productPrices: 'product_prices',
    /**
     * Scratch-and-win cards: one doc per dealer per round, `{round}_{uid}`.
     * CREATED ONLY by the sync worker (service account — it draws the prize, so a
     * dealer can never pick their own); the dealer may stamp `revealedAt` on their
     * own card, admins may mark it redeemed.
     */
    scratchCards: 'scratch_cards',
};
/** Sub-collection under a product document. */
export const SUBCOLLECTIONS = {
    priceHistory: 'price_history',
};
/** The single well-known app_config document id. */
export const APP_CONFIG_DOC = 'global';
/** app_config doc holding the sheet-sync source + column configuration. */
export const SHEET_SYNC_DOC = 'sheetSync';
/** app_config doc holding per-tab presentation config (labels, gaps, share message). */
export const DISPLAY_FORMAT_DOC = 'displayFormat';
/** app_config doc holding per-category offer PDF links. */
export const OFFERS_DOC = 'offers';
/** app_config doc holding the scratch-card toggle, current round and prize table. */
export const SCRATCH_CARD_DOC = 'scratchCard';
export const paths = {
    user: (uid) => `${COLLECTIONS.users}/${uid}`,
    registrationRequest: (id) => `${COLLECTIONS.registrationRequests}/${id}`,
    product: (id) => `${COLLECTIONS.products}/${id}`,
    priceHistory: (productId) => `${COLLECTIONS.products}/${productId}/${SUBCOLLECTIONS.priceHistory}`,
    activityLog: (id) => `${COLLECTIONS.activityLogs}/${id}`,
    pushCampaign: (id) => `${COLLECTIONS.pushCampaigns}/${id}`,
    appConfig: () => `${COLLECTIONS.appConfig}/${APP_CONFIG_DOC}`,
    presence: (uid) => `${COLLECTIONS.presence}/${uid}`,
    admin: (uid) => `${COLLECTIONS.admins}/${uid}`,
    priceViewer: (uid) => `${COLLECTIONS.priceViewers}/${uid}`,
    productPrice: (productId) => `${COLLECTIONS.productPrices}/${productId}`,
    scratchCardConfig: () => `${COLLECTIONS.appConfig}/${SCRATCH_CARD_DOC}`,
    scratchCard: (round, uid) => `${COLLECTIONS.scratchCards}/${scratchCardId(round, uid)}`,
};
/**
 * Deterministic scratch-card doc id: one card per dealer per round. Both the
 * worker (which creates it) and the app (which listens to it) derive the id
 * this way, so neither needs a query to find "my card for this round".
 */
export function scratchCardId(round, uid) {
    return `${round}_${uid}`;
}
/**
 * Deterministic product id. Prefer PART NO. (stable, unique); fall back to
 * model number. Category is included so the same part in two sheets can't
 * collide, and so ids remain human-scannable in the console.
 *
 * Non-alphanumeric characters are collapsed so the id is safe as a doc key.
 */
export function productId(category, partNo, modelNumber) {
    const key = (partNo && partNo.trim()) || modelNumber;
    return `${slug(category)}_${slug(key)}`;
}
function slug(value) {
    return value
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * Short, human claim reference for a card, e.g. "SW-K7Q2M" — what the dealer
 * quotes when ordering and what the admin sees next to the card. Derived from
 * the whole doc id (a hash, NOT the id's tail: the tail is the uid, identical
 * for every card the same dealer ever gets). Same function in both apps, so
 * the two always agree.
 */
export function scratchClaimRef(cardId) {
    let h = 5381;
    for (let i = 0; i < cardId.length; i++)
        h = ((h * 33) ^ cardId.charCodeAt(i)) >>> 0;
    return `SW-${h.toString(36).toUpperCase().padStart(5, '0').slice(-5)}`;
}
//# sourceMappingURL=paths.js.map